import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTimes,
  faInfoCircle,
  faToggleOn,
  faDollarSign,
  faPalette,
  faEnvelope,
  faWrench,
  faUsers,
  faUserEdit,
  faEye,
  faEyeSlash,
  faList,
  faSearch,
  faSortUp,
  faSortDown,
  faTrash,
  faChevronUp,
  faChevronDown,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "ogcommon";
import CompanyLogs from "./CompanyLogs";

// Optionally import axios if your "LeasesTab" needs it:
// import axios from "axios";

/**
 * This component now includes a "Leases" tab which displays and edits
 * the `leases` array returned by GET ?company=... from clientDetails.php.
 *
 * On save/patch, we send:
 * {
 *   ...formData,
 *   users,  // array
 *   leases, // array
 * }
 *
 * The server upserts them based on "LeaseID".
 */

// Utility to standardize input styles
const getInputClasses = (theme, { readOnly = false } = {}) => {
  return `
    w-full px-3 py-2 rounded outline-none border
    transition-colors duration-300
    ${readOnly ? "opacity-70 cursor-not-allowed" : ""}
    ${
      theme === "dark"
        ? "bg-gray-700 text-gray-100 border-gray-600 focus:ring-2 focus:ring-blue-600"
        : "bg-white text-gray-800 border-gray-300 focus:ring-2 focus:ring-blue-500"
    }
  `;
};

const CompanyDashboard = () => {
  const { theme } = useTheme();

  // Main master table array of all companies
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showActive, setShowActive] = useState(true);
  const [logs, setLogs] = useState([]);

  // Whether the big "edit form" is open
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("create"); // or "edit"

  // The main "clients" table fields (53 columns).
  const [formData, setFormData] = useState(() => defaultFormData());

  // sub-tables from that single client
  const [users, setUsers] = useState([]); // from subdomain DB
  const [leases, setLeases] = useState([]); // from subdomain DB
  const [purchasers, setPurchasers] = useState([]); // from subdomain DB
  const [tags, setTags] = useState([]); // from subdomain DB

  // Tab selection in the big modal
  const [activeTab, setActiveTab] = useState("basic");

  // --------------------------------------------------------------------
  // Fetch all companies on mount
  // --------------------------------------------------------------------
  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(
        "https://house.ogpumper.com/api/clientDetails.php"
      );
      const data = await resp.json();
      setCompanies(data);
    } catch (err) {
      console.error("Error fetching companies:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load "showActive" from localStorage if present
    const savedActive = localStorage.getItem("showActive");
    if (savedActive !== null) {
      setShowActive(JSON.parse(savedActive));
    }
    fetchCompanies();
  }, [fetchCompanies]);

  // --------------------------------------------------------------------
  // open the "Edit Company" form => fetch single company
  // --------------------------------------------------------------------
  const openEditForm = useCallback(async (co) => {
    setFormMode("edit");
    setLoading(true);
    try {
      const resp = await fetch(
        `https://house.ogpumper.com/api/clientDetails.php?company=${encodeURIComponent(
          co.company
        )}`
      );
      const data = await resp.json();
      console.log(data);
      if (Array.isArray(data) && data.length > 0) {
        const single = data[0];

        // fill in main form fields
        const newData = { ...defaultFormData() };
        for (const key of Object.keys(newData)) {
          if (single[key] !== undefined) {
            newData[key] = single[key];
          }
        }
        setFormData(newData);

        // sub-table arrays
        setUsers(Array.isArray(single.users) ? single.users : []);
        setLeases(Array.isArray(single.leases) ? single.leases : []);
        setTags(Array.isArray(single.tags) ? single.tags : []);
        setPurchasers(
          Array.isArray(single.purchasers) ? single.purchasers : []
        );
        setLogs(Array.isArray(single.logs) ? single.logs : []);

        console.log("leases");
        console.log(tags);
        console.log(purchasers);

        setActiveTab("basic");
        setShowForm(true);
      } else {
        console.warn("No company found for:", co.company);
        alert("No data returned for that company.");
      }
    } catch (err) {
      console.error("Error fetching single company:", err);
      alert("Could not fetch company details. See console for more info.");
    } finally {
      setLoading(false);
    }
  }, []);

  // --------------------------------------------------------------------
  // default form fields
  // --------------------------------------------------------------------
  function defaultFormData() {
    return {
      subdomain: "",
      name: "",
      user: "",
      pwd: "",
      company: "",
      useBSW: "N",
      use24: "N",
      useMaps: "N",
      useLinePressure: "N",
      ShowWells: "N",
      Active: "Y",
      Notes: "",
      UseWater: "Y",
      UseGas: "Y",
      Type: "O",
      ShowWellPropertyNum: "N",
      OperatorNum: "",
      PumperEditDaysBack: 0,
      UseWaterTanks: "N",
      ShowZeros: "N",
      UseColorCut: "N",
      AppName: "ogPumper",
      AppLogo: "http://ogPumper.com/images/logo.jpg",
      AppAuthor: "ogEndeavors, LLC",
      SupportEmail: "support@ogPumper.com",
      SupportPhone: "325-261-0394",
      SupportAddress: "ogEndeavors, LLC, PO Box 7091, Abilene, TX 79608",
      Bill: "Y",
      Rate1: 5.0,
      Rate2: 5.0,
      Rate3: 5.0,
      GaugeImportFile: "N",
      NetInterestBbls: "N",
      PR: "N",
      ShowPOC: "N",
      CurrentCustomCol: "LeaseID",
      CurrentCustomCol2: "PumperID",
      ShowLoads: "N",
      BillQuarterly: "N",
      ManualWaterLabel: "Water Bbls Gauged",
      ManualWaterLabel2: "*Enter bbls if not entering gauges",
      Data: "N",
      RequireWellReason: "N",
      UseImages: "N",
      PRStartDay: 2,
      CurrentLayout: "O",
      AlwaysOverrideRT: "N",
      ColorCutLabel: "Color Cut Gauge",
      InjCustomCol1: "LeaseID",
      InjCustomCol2: "PumperID",
      NotifyEmails: "",
      UseFieldTicket: "N",
      UseBradenheadPressure: "N",
    };
  }

  // --------------------------------------------------------------------
  // close the main form
  // --------------------------------------------------------------------
  function closeForm() {
    setShowForm(false);
    setFormMode("create");
    setFormData(defaultFormData());
    setUsers([]);
    setLeases([]);
    setActiveTab("basic");
  }

  // --------------------------------------------------------------------
  // Create (POST)
  // --------------------------------------------------------------------
  const handleCreate = useCallback(async () => {
    try {
      const response = await fetch(
        "https://house.ogpumper.com/api/clientDetails.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...formData,
            users,
            leases,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(`Create failed: ${response.statusText}`);
      }
      const result = await response.json();
      console.log("Create success:", result);

      fetchCompanies();
      closeForm();
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  }, [formData, users, leases, fetchCompanies]);

  // --------------------------------------------------------------------
  // Update (PATCH)
  // --------------------------------------------------------------------
  const handleUpdate = useCallback(async () => {
    try {
      const response = await fetch(
        `https://house.ogpumper.com/api/clientDetails.php?company=${encodeURIComponent(
          formData.company
        )}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...formData,
            users,
            leases,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(`Update failed: ${response.statusText}`);
      }
      const result = await response.json();
      console.log("Update success:", result);

      fetchCompanies();
      closeForm();
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  }, [formData, users, leases, fetchCompanies]);

  // --------------------------------------------------------------------
  // Handle "Submit" in the main modal
  // --------------------------------------------------------------------
  const handleSubmitForm = () => {
    if (formMode === "create") {
      handleCreate();
    } else {
      handleUpdate();
    }
  };

  // --------------------------------------------------------------------
  // Toggle "active" vs "inactive" in top bar
  // --------------------------------------------------------------------
  const handleToggleActive = useCallback(() => {
    setShowActive((prev) => {
      const newVal = !prev;
      localStorage.setItem("showActive", JSON.stringify(newVal));
      return newVal;
    });
  }, []);

  // --------------------------------------------------------------------
  // Search + Active filtering
  // --------------------------------------------------------------------
  const filteredCompanies = useMemo(() => {
    const filteredByActive = showActive
      ? companies.filter((co) => co.Active === "Y")
      : companies.filter((co) => co.Active !== "Y");

    if (!searchQuery) return filteredByActive;

    const lower = searchQuery.toLowerCase();
    return filteredByActive.filter((c) =>
      (c.company || "").toLowerCase().includes(lower)
    );
  }, [companies, showActive, searchQuery]);

  // --------------------------------------------------------------------
  // Changes to form fields (text)
  // --------------------------------------------------------------------
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // --------------------------------------------------------------------
  // Toggle "Y/N" checkboxes
  // --------------------------------------------------------------------
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked ? "Y" : "N" }));
  };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        theme === "dark"
          ? "bg-gray-900 text-gray-100"
          : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="container mx-auto px-4 py-8">
        {/* Title + Search + Active/Inactive Toggle */}
        <div className="w-full max-w-4xl mx-auto mb-6">
          <div
            className={`p-6 text-center rounded-xl shadow-md relative ${
              theme === "dark"
                ? "bg-gradient-to-r from-gray-800 to-gray-700"
                : "bg-gradient-to-r from-blue-500 to-indigo-500"
            }`}
          >
            <h2 className="text-3xl font-bold mb-4 text-white">
              Company Dashboard
            </h2>
            <div className="absolute top-4 right-4">
              <button
                onClick={handleToggleActive}
                className="inline-flex items-center justify-center font-bold py-2 px-4 rounded-full shadow-lg transition-transform duration-200 ease-in-out hover:scale-105 bg-white hover:bg-gray-200 text-blue-600"
              >
                <FontAwesomeIcon
                  icon={showActive ? faEye : faEyeSlash}
                  className="mr-2"
                />
                {showActive ? "Active" : "Inactive"}
              </button>
            </div>
            <div className="mb-6 flex flex-col sm:flex-row items-center sm:justify-center gap-4 mt-4">
              {/* Search input */}
              <input
                type="text"
                placeholder="Search by company name..."
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`
                  w-full max-w-md px-4 py-2 rounded-lg shadow-md focus:outline-none
                  focus:ring-2 transition-all duration-300
                  ${
                    theme === "dark"
                      ? "bg-gray-700 text-gray-100 placeholder-gray-400 border-gray-600 focus:ring-blue-600"
                      : "bg-white text-gray-800 placeholder-gray-500 border-gray-300 focus:ring-blue-500"
                  }
                `}
              />
              {/* "Create New Company" button is omitted per instructions */}
            </div>
          </div>
        </div>

        {/* Companies list */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredCompanies.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-300">
            No matches found.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompanies.map((co) => (
              <div
                key={co.subdomain}
                className={`flex flex-col justify-between p-4 rounded-lg shadow-md border transform transition-all duration-200 hover:scale-105 ${
                  theme === "dark"
                    ? "bg-gray-800 hover:bg-gray-700 text-gray-200 border-gray-700"
                    : "bg-white hover:bg-gray-100 text-gray-800 border-gray-200"
                }`}
              >
                <div>
                  <h3 className="font-bold text-xl mb-1">
                    {co.company || "Unknown Company"}
                  </h3>
                  <p
                    className={`text-sm ${
                      theme === "dark" ? "text-gray-300" : "text-gray-500"
                    }`}
                  >
                    Subdomain: {co.subdomain}
                  </p>
                  <p className="text-sm mt-1">
                    Status:{" "}
                    <span
                      className={`${
                        co.Active === "Y" ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {co.Active === "Y" ? "Active" : "Inactive"}
                    </span>
                  </p>
                </div>
                <div className="mt-3 flex space-x-2">
                  {/* EDIT button -> triggers openEditForm */}
                  <button
                    onClick={() => openEditForm(co)}
                    className="flex-1 rounded-full py-2 font-semibold text-center bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    <FontAwesomeIcon icon={faEdit} className="mr-2" />
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Main Modal for create/edit a single company */}
      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div
            className={`w-full max-w-5xl mx-auto p-6 rounded-lg shadow-2xl relative transition-colors duration-300 ${
              theme === "dark" ? "bg-gray-800 text-gray-100" : "bg-white"
            }`}
          >
            {/* Header row */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-4">
                <h2 className="text-2xl font-bold">
                  {formMode === "create"
                    ? "Create New Company"
                    : "Edit Company"}
                </h2>
                {formMode === "edit" && (
                  <span
                    className={`inline-block font-semibold text-lg px-3 py-1 rounded ${
                      theme === "dark"
                        ? "bg-gray-700 text-gray-100"
                        : "bg-gray-200 text-gray-800"
                    }`}
                  >
                    {formData.company}
                  </span>
                )}
              </div>
              <button
                onClick={closeForm}
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 dark:hover:text-gray-200"
              >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </button>
            </div>

            {/* Tab bar */}
            <div className="mb-4 border-b border-gray-300 dark:border-gray-600 flex flex-wrap gap-2">
              <TabButton
                label="Basic Info"
                icon={faInfoCircle}
                active={activeTab === "basic"}
                onClick={() => setActiveTab("basic")}
              />
              <TabButton
                label="Features"
                icon={faToggleOn}
                active={activeTab === "features"}
                onClick={() => setActiveTab("features")}
              />
              <TabButton
                label="Billing"
                icon={faDollarSign}
                active={activeTab === "billing"}
                onClick={() => setActiveTab("billing")}
              />
              <TabButton
                label="Layout/Labels"
                icon={faPalette}
                active={activeTab === "layout"}
                onClick={() => setActiveTab("layout")}
              />
              <TabButton
                label="Support"
                icon={faEnvelope}
                active={activeTab === "support"}
                onClick={() => setActiveTab("support")}
              />
              <TabButton
                label="Misc"
                icon={faWrench}
                active={activeTab === "misc"}
                onClick={() => setActiveTab("misc")}
              />
              <TabButton
                label="Users"
                icon={faUsers}
                active={activeTab === "users"}
                onClick={() => setActiveTab("users")}
              />
              <TabButton
                label="Leases"
                icon={faList}
                active={activeTab === "leases"}
                onClick={() => setActiveTab("leases")}
              />
              <TabButton
                label="Logs"
                icon={faList}
                active={activeTab === "logs"}
                onClick={() => setActiveTab("logs")}
              />
            </div>

            {/* Tab content */}
            <div
              className={`max-h-[60vh] overflow-y-auto p-2 rounded border ${
                theme === "dark" ? "border-gray-600" : "border-gray-200"
              }`}
            >
              {activeTab === "basic" && (
                <BasicInfoTab
                  formData={formData}
                  onChange={handleFieldChange}
                  onToggle={handleCheckboxChange}
                  theme={theme}
                  formMode={formMode}
                />
              )}
              {activeTab === "features" && (
                <FeaturesTab
                  formData={formData}
                  onToggle={handleCheckboxChange}
                  onChange={handleFieldChange}
                  theme={theme}
                />
              )}
              {activeTab === "billing" && (
                <BillingTab
                  formData={formData}
                  onChange={handleFieldChange}
                  onToggle={handleCheckboxChange}
                  theme={theme}
                />
              )}
              {activeTab === "layout" && (
                <LayoutTab
                  formData={formData}
                  onChange={handleFieldChange}
                  theme={theme}
                />
              )}
              {activeTab === "support" && (
                <SupportTab
                  formData={formData}
                  onChange={handleFieldChange}
                  theme={theme}
                />
              )}
              {activeTab === "misc" && (
                <MiscTab
                  formData={formData}
                  onChange={handleFieldChange}
                  onToggle={handleCheckboxChange}
                  theme={theme}
                />
              )}
              {activeTab === "users" && (
                <UsersTab
                  users={users}
                  setUsers={setUsers}
                  theme={theme}
                  companyName={formData.company}
                />
              )}
              {activeTab === "leases" && (
                <LeasesTab
                  leases={leases}
                  setLeases={setLeases}
                  users={users}
                  purchasers={purchasers}
                  tags={tags}
                  theme={theme}
                  companyName={formData.company}
                />
              )}
              {activeTab === "logs" && (
                <CompanyLogs logs={logs} theme={theme} />
              )}
            </div>

            {/* Footer */}
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={closeForm}
                className={`px-4 py-2 rounded ${
                  theme === "dark"
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                    : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                }`}
              >
                Close
              </button>
              <button
                onClick={handleSubmitForm}
                className="px-4 py-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
              >
                {formMode === "create" ? "Create" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** -------------------------------------
 *  Reusable Tab Button
 * ------------------------------------- */
function TabButton({ label, icon, active, onClick }) {
  return (
    <button
      className={`px-4 py-2 font-semibold rounded-t-lg transition-colors ${
        active
          ? "border-b-2 border-blue-500 text-blue-700"
          : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
      }`}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={icon} className="mr-2" />
      {label}
    </button>
  );
}

/** -------------------------------------
 *  BASIC INFO TAB
 * ------------------------------------- */
function BasicInfoTab({ formData, onChange, onToggle, theme, formMode }) {
  const isReadOnly = formMode === "edit"; // subdomain, name, user, pwd => read-only if editing

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* subdomain */}
      <div>
        <label className="block mb-1 font-semibold">Subdomain</label>
        <input
          name="subdomain"
          value={formData.subdomain}
          onChange={onChange}
          readOnly={isReadOnly}
          className={getInputClasses(theme, { readOnly: isReadOnly })}
        />
      </div>
      {/* name => DB Name */}
      <div>
        <label className="block mb-1 font-semibold">DB Name</label>
        <input
          name="name"
          value={formData.name}
          onChange={onChange}
          readOnly={isReadOnly}
          className={getInputClasses(theme, { readOnly: isReadOnly })}
        />
      </div>
      {/* user => DB User */}
      <div>
        <label className="block mb-1 font-semibold">DB User</label>
        <input
          name="user"
          value={formData.user}
          onChange={onChange}
          readOnly={isReadOnly}
          className={getInputClasses(theme, { readOnly: isReadOnly })}
        />
      </div>
      {/* pwd => DB Password */}
      <div>
        <label className="block mb-1 font-semibold">DB Password</label>
        <input
          type="password"
          name="pwd"
          value={formData.pwd}
          onChange={onChange}
          readOnly={isReadOnly}
          className={getInputClasses(theme, { readOnly: isReadOnly })}
        />
      </div>
      {/* company */}
      <div>
        <label className="block mb-1 font-semibold">Company Name</label>
        <input
          name="company"
          value={formData.company}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      {/* Type (O/P) */}
      <div>
        <label className="block mb-1 font-semibold">
          Type (O=Operator, P=Purchaser)
        </label>
        <select
          name="Type"
          value={formData.Type}
          onChange={onChange}
          className={getInputClasses(theme)}
        >
          <option value="O">Operator</option>
          <option value="P">Purchaser</option>
        </select>
      </div>
      {/* Active (Y/N) */}
      <div className="flex items-center space-x-2 mt-2">
        <input
          id="activeToggle"
          type="checkbox"
          name="Active"
          checked={formData.Active === "Y"}
          onChange={onToggle}
        />
        <label htmlFor="activeToggle" className="font-semibold">
          Active?
        </label>
      </div>
      {/* Notes */}
      <div className="md:col-span-2">
        <label className="block mb-1 font-semibold">Notes</label>
        <textarea
          name="Notes"
          value={formData.Notes}
          onChange={onChange}
          rows={4}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  FEATURES TAB
 * ------------------------------------- */
function FeaturesTab({ formData, onToggle, onChange, theme }) {
  const toggles = [
    { name: "useBSW", label: "Use BSW?" },
    { name: "use24", label: "Use 24-Hour Format?" },
    { name: "useMaps", label: "Use Maps?" },
    { name: "useLinePressure", label: "Use Line Pressure?" },
    { name: "ShowWells", label: "Show Wells?" },
    { name: "UseWater", label: "Use Water?" },
    { name: "UseGas", label: "Use Gas?" },
    { name: "ShowWellPropertyNum", label: "Show Well Property Num?" },
    { name: "UseWaterTanks", label: "Use Water Tanks?" },
    { name: "ShowZeros", label: "Show Zero Gauges?" },
    { name: "UseColorCut", label: "Use Color Cut?" },
    { name: "GaugeImportFile", label: "Gauge Import File?" },
    { name: "NetInterestBbls", label: "Net Interest Bbls?" },
    { name: "PR", label: "Use PR?" },
    { name: "ShowPOC", label: "Show POC?" },
    { name: "ShowLoads", label: "Show Loads?" },
    { name: "Data", label: "Use Data?" },
    { name: "RequireWellReason", label: "Require Well Reason?" },
    { name: "UseImages", label: "Use Images?" },
    { name: "UseFieldTicket", label: "Use Field Ticket?" },
    { name: "UseBradenheadPressure", label: "Use Bradenhead Pressure?" },
    { name: "AlwaysOverrideRT", label: "Always Override RT?" },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {toggles.map((t) => (
        <div key={t.name} className="flex items-center space-x-2">
          <input
            id={t.name}
            type="checkbox"
            name={t.name}
            checked={formData[t.name] === "Y"}
            onChange={onToggle}
          />
          <label htmlFor={t.name} className="font-semibold">
            {t.label}
          </label>
        </div>
      ))}
      {/* PumperEditDaysBack */}
      <div>
        <label className="block mb-1 font-semibold">
          Pumper Edit Days Back
        </label>
        <input
          type="number"
          name="PumperEditDaysBack"
          value={formData.PumperEditDaysBack}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  BILLING TAB
 * ------------------------------------- */
function BillingTab({ formData, onChange, onToggle, theme }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Bill */}
      <div className="flex items-center space-x-2">
        <input
          id="billToggle"
          type="checkbox"
          name="Bill"
          checked={formData.Bill === "Y"}
          onChange={onToggle}
        />
        <label htmlFor="billToggle" className="font-semibold">
          Bill?
        </label>
      </div>
      {/* BillQuarterly */}
      <div className="flex items-center space-x-2">
        <input
          id="billQuarterly"
          type="checkbox"
          name="BillQuarterly"
          checked={formData.BillQuarterly === "Y"}
          onChange={onToggle}
        />
        <label htmlFor="billQuarterly" className="font-semibold">
          Bill Quarterly?
        </label>
      </div>
      {/* Rate1 */}
      <div>
        <label className="block mb-1 font-semibold">Rate1</label>
        <input
          type="number"
          step="0.01"
          name="Rate1"
          value={formData.Rate1}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      {/* Rate2 */}
      <div>
        <label className="block mb-1 font-semibold">Rate2</label>
        <input
          type="number"
          step="0.01"
          name="Rate2"
          value={formData.Rate2}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      {/* Rate3 */}
      <div>
        <label className="block mb-1 font-semibold">Rate3</label>
        <input
          type="number"
          step="0.01"
          name="Rate3"
          value={formData.Rate3}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  LAYOUT TAB
 * ------------------------------------- */
function LayoutTab({ formData, onChange, theme }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block mb-1 font-semibold">App Name</label>
        <input
          name="AppName"
          value={formData.AppName}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">App Logo URL</label>
        <input
          name="AppLogo"
          value={formData.AppLogo}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">App Author</label>
        <input
          name="AppAuthor"
          value={formData.AppAuthor}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Current Layout</label>
        <select
          name="CurrentLayout"
          value={formData.CurrentLayout}
          onChange={onChange}
          className={getInputClasses(theme)}
        >
          <option value="O">Original</option>
          <option value="A">Oct2023</option>
        </select>
      </div>
      <div>
        <label className="block mb-1 font-semibold">Color Cut Label</label>
        <input
          name="ColorCutLabel"
          value={formData.ColorCutLabel}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Current Custom Col</label>
        <input
          name="CurrentCustomCol"
          value={formData.CurrentCustomCol}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Current Custom Col2</label>
        <input
          name="CurrentCustomCol2"
          value={formData.CurrentCustomCol2}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Inj Custom Col1</label>
        <input
          name="InjCustomCol1"
          value={formData.InjCustomCol1}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Inj Custom Col2</label>
        <input
          name="InjCustomCol2"
          value={formData.InjCustomCol2}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  SUPPORT TAB
 * ------------------------------------- */
function SupportTab({ formData, onChange, theme }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block mb-1 font-semibold">Support Email</label>
        <input
          name="SupportEmail"
          value={formData.SupportEmail}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Support Phone</label>
        <input
          name="SupportPhone"
          value={formData.SupportPhone}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div className="md:col-span-2">
        <label className="block mb-1 font-semibold">Support Address</label>
        <input
          name="SupportAddress"
          value={formData.SupportAddress}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  MISC TAB
 * ------------------------------------- */
function MiscTab({ formData, onChange, onToggle, theme }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block mb-1 font-semibold">Operator Num</label>
        <input
          name="OperatorNum"
          value={formData.OperatorNum}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">PR Start Day</label>
        <input
          type="number"
          name="PRStartDay"
          value={formData.PRStartDay}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Manual Water Label</label>
        <input
          name="ManualWaterLabel"
          value={formData.ManualWaterLabel}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Manual Water Label 2</label>
        <input
          name="ManualWaterLabel2"
          value={formData.ManualWaterLabel2}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div className="md:col-span-2">
        <label className="block mb-1 font-semibold">Notify Emails</label>
        <textarea
          name="NotifyEmails"
          rows={3}
          value={formData.NotifyEmails}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  USERS TAB
 * ------------------------------------- */

function UsersTab({ users, setUsers, theme, companyName }) {
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const blankUser = {
    UserID: "",
    Sec: "",
    Role: "P", // default to 'P'
    FullName: "",
    Email: "",
    Phone: "",
    GroupID: "", // REQUIRED
    Message: "", // REQUIRED
    Address: "", // REQUIRED
    CityStateZip: "", // REQUIRED
    Misc: "", // REQUIRED
    AutoAdvance: "C", // default 'C'
    google_id: "",
    ChartsPref: "",
    Disabled: "0",
    JobRole: "",
  };

  // ----------------------------------------
  // Add New User
  // ----------------------------------------
  const handleCreateUser = () => {
    setEditingUser({ ...blankUser });
    setShowUserModal(true);
  };

  // ----------------------------------------
  // Edit existing
  // ----------------------------------------
  const handleEditClick = (u) => {
    setEditingUser({ ...u });
    setShowUserModal(true);
  };

  // ----------------------------------------
  // Save => upsert to server + update local state
  // ----------------------------------------
  const handleSaveUser = async () => {
    if (!editingUser) return;

    // ------------------------------------------------------------
    // Basic Validation – only these four fields are mandatory
    // ------------------------------------------------------------
    const required = [
      { key: "UserID", label: "User ID" },
      { key: "Sec", label: "Password" },
      { key: "FullName", label: "Full Name" },
      { key: "Role", label: "Role" },
    ];

    for (const { key, label } of required) {
      const val = (editingUser[key] ?? "").toString().trim();
      if (!val) {
        alert(`${label} is required.`);
        return;
      }
    }

    // ------------------------------------------------------------
    // Minimal payload expected by the API
    // ------------------------------------------------------------
    const mandatoryPayload = {
      UserID: editingUser.UserID.trim(),
      Sec: editingUser.Sec,
      FullName: editingUser.FullName.trim(),
      Role: editingUser.Role,
    };

    try {
      const url = `https://house.ogpumper.com/api/clientDetails.php?company=${encodeURIComponent(
        companyName
      )}&createUser=1`;

      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ users: [mandatoryPayload] }), // API expects an array
      });

      const data = await resp.json();
      console.log("User creation/upsert response:", data);

      if (!resp.ok) {
        throw new Error(
          data?.error ||
            "User creation/upsert request failed with unknown error."
        );
      }

      // ----------------------------------------------------------
      // Local state update (upsert)
      // ----------------------------------------------------------
      const idx = users.findIndex((u) => u.UserID === editingUser.UserID);
      if (idx === -1) {
        setUsers([...users, editingUser]); // new user
      } else {
        const updated = [...users];
        updated[idx] = editingUser; // existing user
        setUsers(updated);
      }
    } catch (err) {
      console.error("Error creating/upserting user:", err);
    } finally {
      setShowUserModal(false);
      setEditingUser(null);
    }
  };

  // ----------------------------------------
  // Cancel editing
  // ----------------------------------------
  const handleCancelUser = () => {
    setEditingUser(null);
    setShowUserModal(false);
  };

  // ----------------------------------------
  // Handle field changes
  // ----------------------------------------
  const handleModalFieldChange = (e) => {
    const { name, value } = e.target;
    setEditingUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setEditingUser((prev) => ({ ...prev, [name]: checked ? "1" : "0" }));
  };

  const getInputClasses = (currentTheme) => {
    return `
      w-full border px-1 py-1 text-sm rounded
      focus:outline-none focus:ring-2 focus:ring-blue-400
      ${
        currentTheme === "dark"
          ? "bg-gray-700 text-gray-100 border-gray-600"
          : "border-gray-300"
      }
    `;
  };

  return (
    <div className="p-2">
      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg font-bold">Users</h3>
        <button
          onClick={handleCreateUser}
          className="flex items-center font-bold py-1 px-3 rounded-md shadow-sm bg-blue-500 text-white hover:bg-blue-600"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add New User
        </button>
      </div>

      {/* Table of existing users */}
      {users && users.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead
              className={`border-b ${
                theme === "dark"
                  ? "bg-gray-700 text-gray-100 border-gray-600"
                  : "bg-gray-100 text-gray-800 border-gray-200"
              }`}
            >
              <tr>
                <th className="py-1 px-2 text-left">UserID</th>
                <th className="py-1 px-2 text-left">Full Name</th>
                <th className="py-1 px-2 text-left">Email</th>
                <th className="py-1 px-2 text-left">Role</th>
                <th className="py-1 px-2 text-left">Disabled?</th>
                <th className="py-1 px-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, idx) => (
                <tr
                  key={idx}
                  className={`border-b ${
                    theme === "dark"
                      ? "border-gray-600 hover:bg-gray-600"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <td className="py-1 px-2">{u.UserID}</td>
                  <td className="py-1 px-2">{u.FullName}</td>
                  <td className="py-1 px-2">{u.Email}</td>
                  <td className="py-1 px-2">{u.Role}</td>
                  <td className="py-1 px-2">
                    {u.Disabled === "1" ? "Yes" : "No"}
                  </td>
                  <td className="py-1 px-2">
                    <button
                      onClick={() => handleEditClick(u)}
                      className="inline-flex items-center px-2 py-1 rounded bg-green-500 hover:bg-green-600 text-white text-xs"
                    >
                      <FontAwesomeIcon icon={faUserEdit} className="mr-1" />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-500 dark:text-gray-300">No users found.</p>
      )}

      {/* User Modal for Add/Edit */}
      {showUserModal && editingUser && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-40 ${
            theme === "dark"
              ? "bg-black bg-opacity-80"
              : "bg-black bg-opacity-50"
          }`}
        >
          <div
            className={`
              w-full max-w-lg p-4 rounded-lg shadow-2xl relative
              ${
                theme === "dark"
                  ? "bg-gray-800 text-gray-100"
                  : "bg-white text-gray-900"
              }
            `}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-base font-bold">
                {editingUser.UserID ? "Edit User" : "Create User"}
              </h4>
              <button
                onClick={handleCancelUser}
                className="p-1 rounded-full text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {/* UserID */}
              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  UserID
                </label>
                <input
                  name="UserID"
                  value={editingUser.UserID}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>
              {/* Password (Sec) */}
              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  Password (Sec)
                </label>
                <input
                  name="Sec"
                  value={editingUser.Sec}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>
              {/* Role */}
              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  Role
                </label>
                <select
                  name="Role"
                  value={editingUser.Role}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                >
                  <option value="A">Admin (A)</option>
                  <option value="P">Pumper (P)</option>
                  <option value="I">Inspector (I)</option>
                  <option value="O">Office (O)</option>
                </select>
              </div>
              {/* Full Name */}
              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  Full Name
                </label>
                <input
                  name="FullName"
                  value={editingUser.FullName}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  Email
                </label>
                <input
                  name="Email"
                  value={editingUser.Email}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>
              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold mb-0.5">
                  Phone
                </label>
                <input
                  name="Phone"
                  value={editingUser.Phone || ""}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>
              {/* Disabled? */}
              <div className="flex items-center mt-1">
                <input
                  id="disabledToggle"
                  type="checkbox"
                  name="Disabled"
                  checked={editingUser.Disabled === "1"}
                  onChange={handleCheckboxChange}
                  className="mr-1"
                />
                <label
                  htmlFor="disabledToggle"
                  className="text-sm font-semibold"
                >
                  Disabled?
                </label>
              </div>
              {/* JobRole */}
              {"JobRole" in editingUser && (
                <div>
                  <label className="block text-sm font-semibold mb-0.5">
                    JobRole
                  </label>
                  <input
                    name="JobRole"
                    value={editingUser.JobRole || ""}
                    onChange={handleModalFieldChange}
                    className={getInputClasses(theme)}
                  />
                </div>
              )}
              {/* GroupID (REQUIRED) */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold mb-0.5">
                  GroupID
                </label>
                <input
                  name="GroupID"
                  value={editingUser.GroupID || ""}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>
              {/* Message (REQUIRED) */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold mb-0.5">
                  Message
                </label>
                <textarea
                  name="Message"
                  value={editingUser.Message || ""}
                  onChange={handleModalFieldChange}
                  rows={2}
                  className={getInputClasses(theme)}
                />
              </div>
              {/* Address (REQUIRED) */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold mb-0.5">
                  Address
                </label>
                <input
                  name="Address"
                  value={editingUser.Address || ""}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>
              {/* CityStateZip (REQUIRED) */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold mb-0.5">
                  City/State/Zip
                </label>
                <input
                  name="CityStateZip"
                  value={editingUser.CityStateZip || ""}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>
              {/* Misc (REQUIRED) */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold mb-0.5">
                  Misc
                </label>
                <input
                  name="Misc"
                  value={editingUser.Misc || ""}
                  onChange={handleModalFieldChange}
                  className={getInputClasses(theme)}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="mt-3 flex justify-end space-x-2">
              <button
                onClick={handleCancelUser}
                className={`px-3 py-1 rounded text-sm
                  ${
                    theme === "dark"
                      ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                      : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                  }
                `}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                className="px-3 py-1 rounded text-sm bg-blue-500 hover:bg-blue-600 text-white"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** -------------------------------------
 *  LEASES TAB
 * ------------------------------------- */

/**
 * This component shows a tab for listing and editing Leases, along with nested Wells/Tanks.
 * It handles create/edit/copy of leases, plus adding/removing wells and tanks.
 * Deleting wells/tanks now calls the DELETE endpoint with wid/tid plus lid.
 */
function LeasesTab({
  leases,
  setLeases,
  users,
  purchasers, // Passed in from props
  tags, // Passed in from props
  theme,
  companyName,
}) {
  const [showLeaseModal, setShowLeaseModal] = useState(false);
  const [editingLease, setEditingLease] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [isEditMode, setIsEditMode] = useState(false);

  // For searching and sorting
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("LeaseID");
  const [sortDirection, setSortDirection] = useState("asc");

  // Expand/collapse states for wells & tanks in the modal
  const [expandedWellIndex, setExpandedWellIndex] = useState(null);
  const [expandedTankIndex, setExpandedTankIndex] = useState(null);

  // Refs for scrolling when we expand wells/tanks
  const wellsSectionRef = useRef(null);
  const tanksSectionRef = useRef(null);

  // Blank lease object for "Add New"
  const blankLease = {
    LeaseID: "",
    LeaseName: "",
    GroupID: "", // ➊ NEW – used for “Group ID” select

    PumperID: "",
    ReliefID: "",
    District: "",
    RRC: "",
    WellType: "OIL",
    ShowOil: "Y",
    ShowWater: "Y",
    ShowGas: "Y",
    Active: "Y",
    Purchaser: "",
    PurchaserLeaseNo: "",
    PropertyNum: "",
    CP: "",
    Tag1: "",
    Tag2: "",
    Tag3: "",
    Tag4: "",
    wells: [],
    tanks: [],
  };

  const isDark = theme === "dark";

  // -------------------------------
  // HELPER STYLES
  // -------------------------------
  const getInputClasses = (isDarkMode) =>
    isDarkMode
      ? "w-full px-2 py-1 bg-gray-700 text-gray-100 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      : "w-full px-2 py-1 bg-white text-gray-800 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500";

  // -------------------------------
  // CREATE/EDIT/COPY LEASE
  // -------------------------------
  const handleCreateLease = () => {
    setEditingLease({ ...blankLease });
    setIsEditMode(false); // <-- not editing an existing lease
    setActiveTab("basic");
    setShowLeaseModal(true);
  };

  const handleEditLease = (lease) => {
    // Clone the lease so we can edit in local state
    const clone = JSON.parse(JSON.stringify(lease));

    // Ensure arrays exist
    if (!Array.isArray(clone.wells)) clone.wells = [];
    if (!Array.isArray(clone.tanks)) clone.tanks = [];

    setEditingLease(clone);
    setIsEditMode(true); // <-- editing existing record

    setActiveTab("basic");
    setShowLeaseModal(true);

    // Reset expansions
    setExpandedWellIndex(null);
    setExpandedTankIndex(null);
  };

  /**
   *  Copies an existing lease but modifies LeaseID (and unique IDs on wells/tanks)
   *  so that it’s treated as a brand-new lease when saved.
   */
  const handleCopyLease = (lease) => {
    const clone = JSON.parse(JSON.stringify(lease));
    // Force the user to supply a brand-new ID
    clone.LeaseID = `${lease.LeaseID}-COPY`;
    // For each well/tank, clear out the UniqID to indicate a new record
    if (Array.isArray(clone.wells)) {
      clone.wells.forEach((w) => {
        w.UniqID = "";
      });
    }
    if (Array.isArray(clone.tanks)) {
      clone.tanks.forEach((t) => {
        t.UniqID = "";
      });
    }

    setEditingLease(clone);
    setIsEditMode(false); // <-- treat copying as new, so LeaseID is editable

    setActiveTab("basic");
    setShowLeaseModal(true);

    // Reset expansions
    setExpandedWellIndex(null);
    setExpandedTankIndex(null);
  };

  const handleCancelLease = () => {
    setEditingLease(null);
    setShowLeaseModal(false);
  };

  // -------------------------------
  // SAVE (POST or PATCH)
  // -------------------------------
  const handleSaveLease = async () => {
    if (!editingLease) return;

    // --------------------------------------------
    // Basic Validation: require LeaseID & LeaseName
    // --------------------------------------------
    if (!editingLease.LeaseID.trim()) {
      alert("A LeaseID is required.");
      return;
    }
    if (!editingLease.LeaseName.trim()) {
      alert("A LeaseName is required.");
      return;
    }
    const existsLocally = leases.some(
      (l) => l.LeaseID === editingLease.LeaseID
    );
    if (!isEditMode && existsLocally) {
      alert(
        `LeaseID "${editingLease.LeaseID}" already exists.  Pick a unique ID.`
      );
      return;
    }

    try {
      // Check if this LeaseID already exists in local array
      const isExisting = leases.some((l) => l.LeaseID === editingLease.LeaseID);
      let url;
      let method;

      if (!isExisting) {
        // brand new => POST with ?createLease=1
        method = "POST";
        url = `https://house.ogpumper.com/api/clientDetails.php?company=${encodeURIComponent(
          companyName
        )}&createLease=1`;
      } else {
        // existing => PATCH
        method = "PATCH";
        url = `https://house.ogpumper.com/api/clientDetails.php?company=${encodeURIComponent(
          companyName
        )}&lid=${encodeURIComponent(editingLease.LeaseID)}`;
      }

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editingLease),
      });

      if (!response.ok) {
        const msg = await response.text();
        throw new Error(
          `Lease save failed. Status: ${response.status}. ${msg}`
        );
      }

      // Update local state
      let updatedLeases;
      if (!isExisting) {
        updatedLeases = [...leases, editingLease];
      } else {
        updatedLeases = leases.map((l) =>
          l.LeaseID === editingLease.LeaseID ? editingLease : l
        );
      }
      setLeases(updatedLeases);

      // Close modal
      setShowLeaseModal(false);
    } catch (err) {
      console.error("Error saving lease:", err);
      alert("Error saving lease. Check console for details.");
    }
  };

  // -------------------------------
  // WELLS / TANKS - ADD / REMOVE
  // -------------------------------
  const handleAddWell = () => {
    setEditingLease((prev) => ({
      ...prev,
      wells: [
        ...prev.wells,
        {
          UniqID: "",
          LeaseID: prev.LeaseID,
          WellID: "",
          Active: "Y",
          PropertyNum: "",
          AllocPct: "0.0000",
          RRC: "",
          CP: "",
        },
      ],
    }));
    // Expand the newly added well
    setTimeout(() => {
      setExpandedWellIndex((prev) =>
        prev?.wells?.length ? prev.wells.length : 0
      );
      wellsSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleRemoveWell = async (well) => {
    if (!well.UniqID) {
      // Well was never saved -> just remove from local state
      setEditingLease((prev) => ({
        ...prev,
        wells: prev.wells.filter((w) => w !== well),
      }));
      return;
    }

    const confirmed = window.confirm(
      `Remove well "${well.WellID}" from server?`
    );
    if (!confirmed) return;

    try {
      // 1) Call the DELETE endpoint
      const url = `https://house.ogpumper.com/api/clientDetails.php?company=${encodeURIComponent(
        companyName
      )}&lid=${encodeURIComponent(
        editingLease.LeaseID
      )}&wid=${encodeURIComponent(well.UniqID)}`;
      const resp = await fetch(url, { method: "DELETE" });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(`Delete well failed: ${resp.status} - ${msg}`);
      }

      // 2) Remove from the modal’s local state
      setEditingLease((prev) => ({
        ...prev,
        wells: prev.wells.filter((w) => w.UniqID !== well.UniqID),
      }));

      // 3) Also remove from the main leases array
      setLeases((prevLeases) =>
        prevLeases.map((lease) => {
          if (lease.LeaseID === editingLease.LeaseID) {
            return {
              ...lease,
              wells: lease.wells.filter((w) => w.UniqID !== well.UniqID),
            };
          }
          return lease;
        })
      );
    } catch (err) {
      console.error("Error removing well:", err);
      alert("Failed to remove well. Check console for details.");
    }
  };

  const handleRemoveTank = async (tank) => {
    if (!tank.UniqID) {
      // Tank was never saved
      setEditingLease((prev) => ({
        ...prev,
        tanks: prev.tanks.filter((t) => t !== tank),
      }));
      return;
    }

    const confirmed = window.confirm(
      `Remove tank "${tank.TankID}" from server? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const url = `https://house.ogpumper.com/api/clientDetails.php?company=${encodeURIComponent(
        companyName
      )}&lid=${encodeURIComponent(
        editingLease.LeaseID
      )}&tid=${encodeURIComponent(tank.UniqID)}`;

      const resp = await fetch(url, { method: "DELETE" });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(`Delete tank failed: ${resp.status} - ${msg}`);
      }

      // 1) Remove from the modal’s local state
      setEditingLease((prev) => ({
        ...prev,
        tanks: prev.tanks.filter((t) => t.UniqID !== tank.UniqID),
      }));

      // 2) Remove from the main leases array
      setLeases((prevLeases) =>
        prevLeases.map((lease) => {
          if (lease.LeaseID === editingLease.LeaseID) {
            return {
              ...lease,
              tanks: lease.tanks.filter((t) => t.UniqID !== tank.UniqID),
            };
          }
          return lease;
        })
      );
    } catch (err) {
      console.error("Error removing tank:", err);
      alert("Failed to remove tank. Check console for details.");
    }
  };

  const handleAddTank = () => {
    setEditingLease((prev) => ({
      ...prev,
      tanks: [
        ...prev.tanks,
        {
          UniqID: "",
          LeaseID: prev.LeaseID,
          TankID: "",
          Size: 0,
          BBLSperInch: "0.000",
          Active: "Y",
          TankType: "T",
          GasCoeff: "0.00",
          ExcludeDrawsFromProd: "N",
          WPTankNum: "",
        },
      ],
    }));
    // Expand the newly added tank
    setTimeout(() => {
      setExpandedTankIndex((prev) =>
        prev?.tanks?.length ? prev.tanks.length : 0
      );
      tanksSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // -------------------------------
  // FIELD HANDLERS
  // -------------------------------
  const handleLeaseFieldChange = (e) => {
    const { name, value } = e.target;
    setEditingLease((prev) => ({ ...prev, [name]: value }));
  };

  const handleLeaseCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setEditingLease((prev) => ({ ...prev, [name]: checked ? "Y" : "N" }));
  };

  const handleWellChange = (index, field, value) => {
    setEditingLease((prev) => {
      const newWells = [...prev.wells];
      newWells[index] = { ...newWells[index], [field]: value };
      return { ...prev, wells: newWells };
    });
  };

  const handleTankChange = (index, field, value) => {
    setEditingLease((prev) => {
      const newTanks = [...prev.tanks];
      newTanks[index] = { ...newTanks[index], [field]: value };
      return { ...prev, tanks: newTanks };
    });
  };

  // -------------------------------
  // TOGGLE EXPAND/COLLAPSE
  // -------------------------------
  const toggleExpandWell = (index) => {
    setExpandedWellIndex((prevIndex) => (prevIndex === index ? null : index));
    if (expandedWellIndex !== index) {
      setTimeout(() => {
        wellsSectionRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  const toggleExpandTank = (index) => {
    setExpandedTankIndex((prevIndex) => (prevIndex === index ? null : index));
    if (expandedTankIndex !== index) {
      setTimeout(() => {
        tanksSectionRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  // -------------------------------
  // SEARCH & SORT
  // -------------------------------
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnKey);
      setSortDirection("asc");
    }
  };

  const sortedAndFilteredLeases = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = leases.filter((lease) => {
      return (
        lease.LeaseID.toLowerCase().includes(lowerSearch) ||
        (lease.LeaseName || "").toLowerCase().includes(lowerSearch) ||
        (lease.RRC || "").toLowerCase().includes(lowerSearch) ||
        (lease.WellType || "").toLowerCase().includes(lowerSearch) || // ➌ NEW
        (lease.PumperID || "").toLowerCase().includes(lowerSearch) ||
        (lease.ReliefID || "").toLowerCase().includes(lowerSearch)
      );
    });

    return filtered.sort((a, b) => {
      const valA = a[sortBy] || "";
      const valB = b[sortBy] || "";
      // Sort strings as strings
      if (typeof valA === "string" && typeof valB === "string") {
        const comparison = valA.localeCompare(valB);
        return sortDirection === "asc" ? comparison : -comparison;
      } else {
        // fallback numeric
        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      }
    });
  }, [leases, searchTerm, sortBy, sortDirection]);

  const pumperOptions = users || [];
  const reliefOptions = users || [];

  // -------------------------------
  // RENDER
  // -------------------------------
  return (
    <div className="p-2">
      {/* Top bar: search & add-new */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-3">
        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className={`absolute left-3 top-2.5 ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}
          />
          <input
            type="text"
            placeholder="Search leases..."
            value={searchTerm}
            onChange={handleSearchChange}
            className={`pl-9 pr-4 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isDark
                ? "bg-gray-700 text-gray-100 border border-gray-600"
                : "bg-white text-gray-700 border border-gray-300"
            }`}
          />
        </div>
        <button
          onClick={handleCreateLease}
          className="inline-flex items-center justify-center font-semibold py-1 px-3 rounded-md shadow-sm bg-blue-500 text-white hover:bg-blue-600"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-1" />
          Add New Lease
        </button>
      </div>

      {/* Leases Table */}
      {sortedAndFilteredLeases && sortedAndFilteredLeases.length > 0 ? (
        <div className="overflow-x-auto rounded-md shadow-sm">
          <table className="min-w-full text-sm">
            <thead
              className={`border-b ${
                isDark
                  ? "bg-gray-700 text-gray-100 border-gray-600"
                  : "bg-gray-100 text-gray-800 border-gray-200"
              }`}
            >
              <tr>
                <SortableHeader
                  label="LeaseID"
                  columnKey="LeaseID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="LeaseName"
                  columnKey="LeaseName"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="RRC #"
                  columnKey="RRC"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />

                <SortableHeader
                  label="WellType"
                  columnKey="WellType"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Pumper"
                  columnKey="PumperID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Relief"
                  columnKey="ReliefID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Active"
                  columnKey="Active"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <th className="py-2 px-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredLeases.map((lease, idx) => (
                <tr
                  key={idx}
                  className={`border-b ${
                    isDark
                      ? "border-gray-600 hover:bg-gray-600"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <td className="py-2 px-4">{lease.LeaseID}</td>
                  <td className="py-2 px-4">{lease.LeaseName}</td>
                  <td className="py-2 px-4">{lease.RRC}</td>
                  <td className="py-2 px-4">{lease.WellType}</td>

                  <td className="py-2 px-4">{lease.PumperID || ""}</td>
                  <td className="py-2 px-4">{lease.ReliefID || ""}</td>
                  <td className="py-2 px-4">
                    {lease.Active === "Y" ? "Yes" : "No"}
                  </td>
                  <td className="py-2 px-4">
                    <button
                      onClick={() => handleEditLease(lease)}
                      className="inline-flex items-center px-2 py-1 rounded bg-green-500 hover:bg-green-600 text-white"
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleCopyLease(lease)}
                      className="inline-flex items-center px-2 py-1 rounded bg-purple-500 hover:bg-purple-600 text-white ml-2"
                    >
                      <FontAwesomeIcon icon={faCopy} className="mr-1" />
                      Copy
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className={`${isDark ? "text-gray-300" : "text-gray-500"} mt-4`}>
          No leases found.
        </p>
      )}

      {/* Lease Modal (Create or Edit) */}
      {showLeaseModal && editingLease && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-40 ${
            isDark ? "bg-black bg-opacity-80" : "bg-black bg-opacity-50"
          }`}
        >
          <div
            className={`w-full max-w-2xl p-4 rounded-lg shadow-2xl relative transition-colors duration-300 ${
              isDark ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"
            }`}
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-lg font-bold">
                {editingLease.LeaseID &&
                leases.some((l) => l.LeaseID === editingLease.LeaseID)
                  ? "Edit Lease"
                  : "Create Lease"}
              </h4>
              <button
                onClick={handleCancelLease}
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Tab Buttons */}
            <div className="mb-4 flex space-x-2">
              <button
                onClick={() => setActiveTab("basic")}
                className={`px-3 py-1 rounded ${
                  activeTab === "basic"
                    ? "bg-blue-500 text-white"
                    : isDark
                    ? "bg-gray-700 text-gray-100"
                    : "bg-gray-300 text-gray-800"
                }`}
              >
                Basic Info
              </button>
              <button
                onClick={() => setActiveTab("wells")}
                className={`px-3 py-1 rounded ${
                  activeTab === "wells"
                    ? "bg-blue-500 text-white"
                    : isDark
                    ? "bg-gray-700 text-gray-100"
                    : "bg-gray-300 text-gray-800"
                }`}
              >
                Wells
              </button>
              <button
                onClick={() => setActiveTab("tanks")}
                className={`px-3 py-1 rounded ${
                  activeTab === "tanks"
                    ? "bg-blue-500 text-white"
                    : isDark
                    ? "bg-gray-700 text-gray-100"
                    : "bg-gray-300 text-gray-800"
                }`}
              >
                Tanks
              </button>
            </div>

            {/* TABS */}
            {activeTab === "basic" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4 text-sm">
                {/* LeaseID (read-only) */}

                <div>
                  <label className="block mb-1 font-semibold">LeaseID</label>
                  <input
                    name="LeaseID"
                    value={editingLease.LeaseID}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                    readOnly={isEditMode} // <-- read-only only if we're editing
                  />
                </div>

                {/* LeaseName */}
                <div>
                  <label className="block mb-1 font-semibold">LeaseName</label>
                  <input
                    name="LeaseName"
                    value={editingLease.LeaseName}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                {/* PumperID dropdown */}
                <div>
                  <label className="block mb-1 font-semibold">Pumper</label>
                  <select
                    name="PumperID"
                    value={editingLease.PumperID || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {pumperOptions?.map((u) => (
                      <option key={u.UserID} value={u.UserID}>
                        {u.UserID} - {u.FullName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ReliefID dropdown */}
                <div>
                  <label className="block mb-1 font-semibold">Relief</label>
                  <select
                    name="ReliefID"
                    value={editingLease.ReliefID || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {reliefOptions?.map((u) => (
                      <option key={u.UserID} value={u.UserID}>
                        {u.UserID} - {u.FullName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Active => Y/N */}
                <div className="flex items-center space-x-2 mt-1">
                  <input
                    id="leaseActiveToggle"
                    type="checkbox"
                    name="Active"
                    checked={editingLease.Active === "Y"}
                    onChange={handleLeaseCheckboxChange}
                  />
                  <label htmlFor="leaseActiveToggle" className="font-semibold">
                    Active?
                  </label>
                </div>

                {/* RRC */}
                <div>
                  <label className="block mb-1 font-semibold">RRC</label>
                  <input
                    name="RRC"
                    value={editingLease.RRC}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                {/* District */}
                <div>
                  <label className="block mb-1 font-semibold">District</label>
                  <input
                    name="District"
                    value={editingLease.District}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                {/* WellType */}
                <div>
                  <label className="block mb-1 font-semibold">WellType</label>
                  <select
                    name="WellType"
                    value={editingLease.WellType || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="OIL">OIL</option>
                    <option value="GAS">GAS</option>
                    <option value="INJ">INJ</option>
                  </select>
                </div>

                {/* Group ID */}
                <div>
                  <label className="block mb-1 font-semibold">Group ID</label>
                  <select
                    name="GroupID"
                    value={editingLease.GroupID || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>{" "}
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagID} – {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ShowOil => Y/N */}
                <div className="flex items-center space-x-2 mt-1">
                  <input
                    id="showOilToggle"
                    type="checkbox"
                    name="ShowOil"
                    checked={editingLease.ShowOil === "Y"}
                    onChange={handleLeaseCheckboxChange}
                  />
                  <label htmlFor="showOilToggle" className="font-semibold">
                    Show Oil?
                  </label>
                </div>

                {/* ShowWater => Y/N */}
                <div className="flex items-center space-x-2 mt-1">
                  <input
                    id="showWaterToggle"
                    type="checkbox"
                    name="ShowWater"
                    checked={editingLease.ShowWater === "Y"}
                    onChange={handleLeaseCheckboxChange}
                  />
                  <label htmlFor="showWaterToggle" className="font-semibold">
                    Show Water?
                  </label>
                </div>

                {/* ShowGas => Y/N */}
                <div className="flex items-center space-x-2 mt-1">
                  <input
                    id="showGasToggle"
                    type="checkbox"
                    name="ShowGas"
                    checked={editingLease.ShowGas === "Y"}
                    onChange={handleLeaseCheckboxChange}
                  />
                  <label htmlFor="showGasToggle" className="font-semibold">
                    Show Gas?
                  </label>
                </div>

                {/* Purchaser dropdown */}
                <div>
                  <label className="block mb-1 font-semibold">Purchaser</label>
                  <select
                    name="Purchaser"
                    value={editingLease.Purchaser || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {purchasers?.map((p) => (
                      <option key={p.PurchaserID} value={p.PurchaserID}>
                        {p.PurchaserID} - {p.PurchaserName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* PurchaserLeaseNo */}
                <div>
                  <label className="block mb-1 font-semibold">
                    Purchaser Lease No
                  </label>
                  <input
                    name="PurchaserLeaseNo"
                    value={editingLease.PurchaserLeaseNo}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                {/* CP */}
                <div>
                  <label className="block mb-1 font-semibold">CP</label>
                  <input
                    name="CP"
                    value={editingLease.CP || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                {/* PropertyNum */}
                <div>
                  <label className="block mb-1 font-semibold">
                    External Property #
                  </label>
                  <input
                    name="PropertyNum"
                    value={editingLease.PropertyNum || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                {/* Tag1 */}
                <div>
                  <label className="block mb-1 font-semibold">Tag 1</label>
                  <select
                    name="Tag1"
                    value={editingLease.Tag1 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Tag2 */}
                <div>
                  <label className="block mb-1 font-semibold">Tag 2</label>
                  <select
                    name="Tag2"
                    value={editingLease.Tag2 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Tag3 */}
                <div>
                  <label className="block mb-1 font-semibold">Tag 3</label>
                  <select
                    name="Tag3"
                    value={editingLease.Tag3 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Tag4 */}
                <div>
                  <label className="block mb-1 font-semibold">Tag 4</label>
                  <select
                    name="Tag4"
                    value={editingLease.Tag4 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {activeTab === "wells" && (
              <div ref={wellsSectionRef} className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-semibold">Wells</h5>
                  <button
                    onClick={handleAddWell}
                    className="inline-flex items-center px-2 py-1 rounded bg-gray-300 hover:bg-gray-400 text-gray-800"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                    Add Well
                  </button>
                </div>
                {editingLease.wells && editingLease.wells.length > 0 ? (
                  <div className="space-y-2">
                    {editingLease.wells.map((well, index) => (
                      <div
                        key={index}
                        className={`border p-2 rounded ${
                          isDark ? "border-gray-600" : "border-gray-300"
                        }`}
                      >
                        {/* Expand/Collapse header */}
                        <div
                          onClick={() => toggleExpandWell(index)}
                          className="flex justify-between items-center cursor-pointer"
                        >
                          <span className="font-semibold">
                            Well: {well.WellID || "(New)"}
                          </span>
                          <FontAwesomeIcon
                            icon={
                              expandedWellIndex === index
                                ? faChevronUp
                                : faChevronDown
                            }
                          />
                        </div>

                        {/* Conditionally render details if expanded */}
                        {expandedWellIndex === index && (
                          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <label className="block font-semibold">
                                WellID
                              </label>
                              <input
                                className={getInputClasses(isDark)}
                                value={well.WellID || ""}
                                onChange={(e) =>
                                  handleWellChange(
                                    index,
                                    "WellID",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div>
                              <label className="block font-semibold">
                                Active
                              </label>
                              <select
                                className={getInputClasses(isDark)}
                                value={well.Active || "Y"}
                                onChange={(e) =>
                                  handleWellChange(
                                    index,
                                    "Active",
                                    e.target.value
                                  )
                                }
                              >
                                <option value="Y">Yes</option>
                                <option value="N">No</option>
                              </select>
                            </div>
                            <div>
                              <label className="block font-semibold">
                                PropertyNum
                              </label>
                              <input
                                className={getInputClasses(isDark)}
                                value={well.PropertyNum || ""}
                                onChange={(e) =>
                                  handleWellChange(
                                    index,
                                    "PropertyNum",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div>
                              <label className="block font-semibold">
                                AllocPct (API)
                              </label>
                              <input
                                className={getInputClasses(isDark)}
                                value={well.AllocPct || "0.0000"}
                                onChange={(e) =>
                                  handleWellChange(
                                    index,
                                    "AllocPct",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div>
                              <label className="block font-semibold">RRC</label>
                              <input
                                className={getInputClasses(isDark)}
                                value={well.RRCLeaseID || ""}
                                onChange={(e) =>
                                  handleWellChange(
                                    index,
                                    "RRCLeaseID",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div>
                              <label className="block font-semibold">CP</label>
                              <input
                                className={getInputClasses(isDark)}
                                value={well.CP || ""}
                                onChange={(e) =>
                                  handleWellChange(index, "CP", e.target.value)
                                }
                              />
                            </div>
                            <div className="col-span-2 flex justify-end mt-2">
                              <button
                                onClick={() => handleRemoveWell(well)}
                                className="inline-flex items-center px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white text-xs"
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="mr-1"
                                />
                                Remove
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm italic">No wells yet.</p>
                )}
              </div>
            )}

            {activeTab === "tanks" && (
              <div ref={tanksSectionRef} className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-semibold">Tanks</h5>
                  <button
                    onClick={handleAddTank}
                    className="inline-flex items-center px-2 py-1 rounded bg-gray-300 hover:bg-gray-400 text-gray-800"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                    Add Tank
                  </button>
                </div>
                {editingLease.tanks && editingLease.tanks.length > 0 ? (
                  <div className="space-y-2">
                    {editingLease.tanks.map((tank, index) => (
                      <div
                        key={index}
                        className={`border p-2 rounded ${
                          isDark ? "border-gray-600" : "border-gray-300"
                        }`}
                      >
                        {/* Expand/Collapse header */}
                        <div
                          onClick={() => toggleExpandTank(index)}
                          className="flex justify-between items-center cursor-pointer"
                        >
                          <span className="font-semibold">
                            Tank: {tank.TankID || "(New)"}
                          </span>
                          <FontAwesomeIcon
                            icon={
                              expandedTankIndex === index
                                ? faChevronUp
                                : faChevronDown
                            }
                          />
                        </div>

                        {/* Conditionally render details if expanded */}
                        {expandedTankIndex === index && (
                          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <label className="block font-semibold">
                                TankID
                              </label>
                              <input
                                className={getInputClasses(isDark)}
                                value={tank.TankID || ""}
                                onChange={(e) =>
                                  handleTankChange(
                                    index,
                                    "TankID",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div>
                              <label className="block font-semibold">
                                Active
                              </label>
                              <select
                                className={getInputClasses(isDark)}
                                value={tank.Active || "Y"}
                                onChange={(e) =>
                                  handleTankChange(
                                    index,
                                    "Active",
                                    e.target.value
                                  )
                                }
                              >
                                <option value="Y">Yes</option>
                                <option value="N">No</option>
                              </select>
                            </div>
                            <div>
                              <label className="block font-semibold">
                                Size
                              </label>
                              <input
                                type="number"
                                className={getInputClasses(isDark)}
                                value={tank.Size || 0}
                                onChange={(e) =>
                                  handleTankChange(
                                    index,
                                    "Size",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div>
                              <label className="block font-semibold">
                                BBLSperInch
                              </label>
                              <input
                                type="number"
                                className={getInputClasses(isDark)}
                                value={tank.BBLSperInch || "0.000"}
                                onChange={(e) =>
                                  handleTankChange(
                                    index,
                                    "BBLSperInch",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div>
                              <label className="block font-semibold">
                                TankType
                              </label>
                              <select
                                className={getInputClasses(isDark)}
                                value={tank.TankType || "T"}
                                onChange={(e) =>
                                  handleTankChange(
                                    index,
                                    "TankType",
                                    e.target.value
                                  )
                                }
                              >
                                <option value="T">Tank</option>
                                <option value="W">Water</option>
                                <option value="F">Frac</option>
                              </select>
                            </div>
                            <div>
                              <label className="block font-semibold">
                                GasCoeff
                              </label>
                              <input
                                type="number"
                                className={getInputClasses(isDark)}
                                value={tank.GasCoeff || "0.00"}
                                onChange={(e) =>
                                  handleTankChange(
                                    index,
                                    "GasCoeff",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div>
                              <label className="block font-semibold">
                                ExcludeDrawsFromProd
                              </label>
                              <select
                                className={getInputClasses(isDark)}
                                value={tank.ExcludeDrawsFromProd || "N"}
                                onChange={(e) =>
                                  handleTankChange(
                                    index,
                                    "ExcludeDrawsFromProd",
                                    e.target.value
                                  )
                                }
                              >
                                <option value="N">No</option>
                                <option value="Y">Yes</option>
                              </select>
                            </div>
                            <div>
                              <label className="block font-semibold">
                                WPTankNum
                              </label>
                              <input
                                className={getInputClasses(isDark)}
                                value={tank.WPTankNum || ""}
                                onChange={(e) =>
                                  handleTankChange(
                                    index,
                                    "WPTankNum",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div className="col-span-2 flex justify-end mt-2">
                              <button
                                onClick={() => handleRemoveTank(tank)}
                                className="inline-flex items-center px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white text-xs"
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="mr-1"
                                />
                                Remove
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm italic">No tanks yet.</p>
                )}
              </div>
            )}

            {/* Modal Footer */}
            <div className="mt-3 flex justify-end space-x-2">
              <button
                onClick={handleCancelLease}
                className={`px-3 py-1 rounded ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLease}
                className="px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white"
              >
                Save Lease
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * SortableHeader is a reusable table-header cell for sorting.
 * It shows an up/down icon if the column is currently sorted.
 */
function SortableHeader({
  label,
  columnKey,
  sortBy,
  sortDirection,
  onSort,
  theme,
}) {
  const isSortedColumn = sortBy === columnKey;
  const isDark = theme === "dark";

  return (
    <th
      className="py-2 px-4 text-left cursor-pointer select-none"
      onClick={() => onSort(columnKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {isSortedColumn && (
          <FontAwesomeIcon
            icon={sortDirection === "asc" ? faSortUp : faSortDown}
            className={isDark ? "text-gray-200" : "text-gray-600"}
          />
        )}
      </div>
    </th>
  );
}

export default CompanyDashboard;
